import { Component, inject, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgbModal, NgbModalOptions, NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { GraphTrendIconComponent } from '../../../../../../_shared/components/graphs/graph-trend-icon/graph-trend-icon.component';
import { MultiLineChartComponent, MultiLineChartDataPoint, MultiLineChartSeries, LegendItem } from '../../../../../../_shared/components/graphs/multi-line-chart/multi-line-chart.component';
import { AuthService, FilterService } from '../../../../../../_shared/services';
import { ErrorHandlerService } from '../../../../../../_shared/services/error-handler.service';
import { MaintenanceCostingService } from '../../../services/maintenance-costing.service';
import { differenceInDays } from 'date-fns';
import { DateInterval } from '../../../../../../_shared/enums';
import { SummaryRequest } from '../../../../../../_shared/models/summary-request.model';
import { hasCompleteDateRange, splitWidgetFilter } from '../../../../../../_shared/helpers/widget-filter.helper';
import { MaintenanceInsightsResponse } from '../../../../maintenance-dashboard/models/maintenance-dashboard-response.model';
import { calculateChartYTicks } from '../../../../../../_shared/helpers/chart-y-ticks.helper';
import { groupAverageCpk } from '../../../helpers/cpk-grouping.helper';
import { CPK_DEFAULT_Y_TICKS, CPK_TICK_DECIMALS, CPK_TICK_INTERVALS } from '../../../constants/cpk-chart.constants';
import { EmptyStateComponent } from '../../../../../../_shared/components/states/empty-state/empty-state.component';
import { CpkTrendModalComponent } from '../../modals/cpk-trend-modal/cpk-trend-modal.component';
import { WidgetFilterData } from '../../../../../../_shared/models/filters/widget-filter-data.model';
import { Subject } from 'rxjs';
import { CurrencyHelper } from '../../../../../../_shared/helpers/currency/currency-map.helper';
import { formatValue } from '../../../../../../_shared/helpers/kpiWidget/kpi-formatters.helper';
import { TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-cpk-trend',
  standalone: true,
  imports: [CommonModule, NgbTooltip, GraphTrendIconComponent, MultiLineChartComponent, EmptyStateComponent],
  templateUrl: './cpk-trend.component.html',
  styleUrl: './cpk-trend.component.scss'
})
export class CpkTrendComponent implements OnInit, OnDestroy {
  private filterService = inject(FilterService);
  private modalService = inject(NgbModal);
  private costingService = inject(MaintenanceCostingService);
  private errorHandlerService: ErrorHandlerService = inject(ErrorHandlerService);
  private authService = inject(AuthService);
  private readonly transloco = inject(TranslocoService);

  chartData: MultiLineChartDataPoint[] = [];
  private readonly defaultYTicks = CPK_DEFAULT_Y_TICKS;
  yTicks: number[] = this.defaultYTicks;
  readonly chartHeight = 145;

  readonly series: MultiLineChartSeries[] = [
    { key: 'inHouse', color: '#8D63C3', label: this.transloco.translate('maintenanceCosting.charts.cpkTrend.legend.inHouse') },
    { key: 'outsourced', color: '#F4BC2B', label: this.transloco.translate('maintenanceCosting.charts.cpkTrend.legend.outsourced') }
  ];

  readonly legendItems: LegendItem[] = [
    { legend: this.transloco.translate('maintenanceCosting.charts.cpkTrend.legend.inHouse'), color: '#8D63C3' },
    { legend: this.transloco.translate('maintenanceCosting.charts.cpkTrend.legend.outsourced'), color: '#F4BC2B' }
  ];

  readonly yAxisFormatter = (value: any): string => {
    return formatValue(Number(value), { currencySymbol: this.currencySymbol });
  };

  get title(): string {
    return this.transloco.translate('maintenanceCosting.charts.cpkTrend.title');
  }

  get tooltip(): string {
    return this.transloco.translate('maintenanceCosting.charts.cpkTrend.tooltip');
  }

  isLoading: boolean = true;
  totalValue: number = 0;
  percentageChange: number = 0;
  widgetFilter: WidgetFilterData;
  currencySymbol: string = '';
  currencyCode: string = '';

  private cancelSubject: Subject<void> = new Subject<void>();

  constructor() {
    this.filterService.filterUpdated$
      .pipe(takeUntilDestroyed())
      .subscribe(async (filter) => {
        if (!hasCompleteDateRange(filter)) return;

        this.widgetFilter = filter;
        await this.loadCpkData();
      });
  }

  ngOnInit(): void {
    this.widgetFilter = this.filterService.filter;
    this.currencyCode = this.authService.getUserSettings()?.currencyCode ?? 'ZAR';
    this.currencySymbol = CurrencyHelper.getCurrencySymbol(this.currencyCode);

    // The shared filter may still hold another page's selection
    if (hasCompleteDateRange(this.widgetFilter)) {
      this.loadCpkData();
    }
  }

  ngOnDestroy(): void {
    this.cancelSubject.next();
    this.cancelSubject.complete();
  }

  private async loadCpkData(): Promise<void> {
    this.isLoading = true;

    try {
      this.cancelSubject.next();

      const request: SummaryRequest = {
        timeFrom: this.widgetFilter.startDate!,
        timeTo: this.widgetFilter.endDate!,
        interval: this.widgetFilter.interval,
        accountIds: this.widgetFilter.accountIds,
        previousTimeFrom: this.widgetFilter.previousCycleStartDate!,
        previousTimeTo: this.widgetFilter.previousCycleEndDate!
      };

      const [inHouseResponse, outsourcedResponse] = await Promise.all([
        this.costingService.InHouseCostPerKm(request, this.cancelSubject),
        this.costingService.OutSourceCostPerKm(request, this.cancelSubject)
      ]);

      this.chartData = this.processCpkData(inHouseResponse.data?.data ?? [], outsourcedResponse.data?.data ?? []);
      this.totalValue = inHouseResponse.data?.totalValue ?? 0;
      this.percentageChange = inHouseResponse.data?.percentageChange ?? 0;
      this.calculateYTicks(this.chartData);

      this.isLoading = false;
    } catch (error: any) {
      this.errorHandlerService.handleError(error, {
        callback: () => {
          this.chartData = [];
          this.isLoading = false;
        }
      });
    }
  }

  private processCpkData(
    inHouseData: MaintenanceInsightsResponse[],
    outsourcedData: MaintenanceInsightsResponse[]
  ): MultiLineChartDataPoint[] {
    if (inHouseData.length === 0 && outsourcedData.length === 0) return [];

    const daysDifference = differenceInDays(this.widgetFilter.endDate!, this.widgetFilter.startDate!);
    const groupingType = this.determineGroupingType(daysDifference, this.widgetFilter.interval);

    const averaged = groupAverageCpk([
      { label: 'inHouse', data: inHouseData },
      { label: 'outsourced', data: outsourcedData }
    ], groupingType, this.widgetFilter.startDate!, this.widgetFilter.endDate!);

    return averaged.map(item => ({
      x: item.label,
      inHouse: item['inHouse'],
      outsourced: item['outsourced']
    }));
  }

  private determineGroupingType(daysDifference: number, interval: DateInterval): 'hourly' | 'daily' | 'weekly' {
    if (daysDifference === 0) return 'hourly';
    if (daysDifference <= 7) return 'daily';
    return 'weekly';
  }

  private calculateYTicks(data: MultiLineChartDataPoint[]): void {
    const allValues = data.flatMap(point => this.series
      .map(s => point[s.key])
      .filter((value): value is number => typeof value === 'number'));

    this.yTicks = calculateChartYTicks(allValues, this.defaultYTicks, CPK_TICK_INTERVALS, CPK_TICK_DECIMALS);
  }

  formatPercentChange(): string {
    const sign = this.percentageChange > 0 ? '+' : '';
    return `${sign}${this.percentageChange.toLocaleString('default', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}%`;
  }

  isUptrendPositive(): boolean {
    return false; // CPK decrease is positive (lower cost per km)
  }

  formatValue(): string {
    return formatValue(this.totalValue, { currencySymbol: this.currencySymbol });
  }

  // An arrow function so it keeps this component's currency when the chart calls it
  formatTooltipValue = (value: number): string => {
    return `${this.currencySymbol}${value.toLocaleString('default', { maximumFractionDigits: 2 })}`;
  };

  hasData(): boolean {
    if (!this.chartData || this.chartData.length === 0) return false;

    return this.chartData.some(item => {
      // Check all series keys for non-zero values
      return this.series.some(s => {
        const value = Number(item[s.key]) || 0;
        return value > 0;
      });
    });
  }

  openModal(): void {
    const modalOptions: NgbModalOptions = {
      centered: true,
      backdrop: 'static',
      size: 'xl',
    };

    const modalRef = this.modalService.open(CpkTrendModalComponent, modalOptions);
    Object.assign(modalRef.componentInstance, splitWidgetFilter(this.filterService.filter));
  }
}